import apiClient from '@/lib/api-client';

export type Trend = 'improving' | 'stable' | 'declining';

export interface TrendPoint {
  month: string;
  score: number;
}

export interface CustomerTrends {
  trend: Trend;
  history: TrendPoint[];
}

export type BatchRunStatus = 'running' | 'completed' | 'failed';

export interface BatchRun {
  batchId: string;
  startedAt: string;
  completedAt: string | null;
  status: BatchRunStatus;
  productCount: number;
  errorCount: number;
}

export interface BatchHistory {
  batches: BatchRun[];
}

export interface EvaluateResult {
  cinXmlId: string;
  overallScore: number;
  berekenDatum: string;
  /** The rule basis DQS actually used (e.g. "gpc-specific") — displayed as-is, never derived frontend-side (FR26). */
  ruleBasis: string;
}

export async function getTrends(gln: string, months = 6): Promise<{ data: CustomerTrends }> {
  const response = await apiClient.get<{ data: CustomerTrends }>(
    `/data-quality/${gln}/trends?months=${months}`,
  );
  return response.data;
}

export async function getBatchHistory(gln: string): Promise<{ data: BatchHistory }> {
  const response = await apiClient.get<{ data: BatchHistory }>(
    `/data-quality/${gln}/batch-history`,
  );
  return response.data;
}

export async function getBatchStatus(batchId: string): Promise<{ data: BatchRun }> {
  const response = await apiClient.get<{ data: BatchRun }>(
    `/data-quality/batch/${batchId}/status`,
  );
  return response.data;
}

export async function evaluateProduct(gln: string, cinXmlId: string): Promise<{ data: EvaluateResult }> {
  const response = await apiClient.post<{ data: EvaluateResult }>(
    '/data-quality/evaluate',
    { gln, cinXmlId },
  );
  return response.data;
}

export type AlertStatus = 'open' | 'resolved';

/**
 * fu-3/AC2: DQS's `/dashboard/alerts` models rule-anomaly alerts (a completeness rule being
 * overridden too often, or auto-disabled as a result) — NOT per-product data-quality findings.
 * There is no `gln`/`cinXmlId`/`severity`/`message` field on the real API; the UI builds a
 * localized message from these structured fields instead (never a backend-supplied string).
 */
export type AlertAnomalyType = 'high_override_rate' | 'auto_disabled';

export interface Alert {
  alertId: string;
  ruleId: string;
  ruleName: string;
  anomalyType: AlertAnomalyType;
  detectedAt: string;
  timeWindowMinutes: number;
  overrideCount: number;
  totalFindingsCount: number;
  overridePercentage: number;
  autoDisabled: boolean;
  autoDisabledAt: string | null;
  status: AlertStatus;
}

export interface PaginatedEnvelope<T> {
  data: T[];
  meta: { total: number; page: number; pageSize: number };
}

export interface AlertsQuery {
  gln?: string;
  status?: AlertStatus;
  page?: number;
  pageSize?: number;
}

/**
 * fu-3/AC2: DQS's bulk-resolve response reports only an aggregate `resolvedCount` — never a
 * per-ID `resolved`/`failed` breakdown (that shape does not exist on the real API).
 * `requestedCount` lets the caller detect a partial failure (resolvedCount < requestedCount).
 */
export interface BulkResolveResult {
  resolvedCount: number;
  requestedCount: number;
}

/** fu-3/AC1+AC2: real DQS missing-GPC item shape — no `gtin`/`productDescription` lookup exists
 * on this report; `gpcCode` and the last-evaluation date replace them. */
export interface MissingGpcItem {
  cinXmlId: string;
  gpcCode: string;
  gln: string;
  laatsteEvaluatieDatum: string;
}

export async function getAlerts(query: AlertsQuery): Promise<PaginatedEnvelope<Alert>> {
  const params = new URLSearchParams();
  if (query.gln) params.set('gln', query.gln);
  if (query.status) params.set('status', query.status);
  if (query.page) params.set('page', String(query.page));
  if (query.pageSize) params.set('pageSize', String(query.pageSize));
  const response = await apiClient.get<PaginatedEnvelope<Alert>>(`/data-quality/alerts?${params.toString()}`);
  return response.data;
}

export async function resolveAlert(alertId: string): Promise<void> {
  await apiClient.patch(`/data-quality/alerts/${alertId}/resolve`);
}

export async function bulkResolveAlerts(alertIds: string[]): Promise<{ data: BulkResolveResult }> {
  const response = await apiClient.post<{ data: BulkResolveResult }>('/data-quality/alerts/bulk-resolve', { alertIds });
  return response.data;
}

export async function getMissingGpcReport(gln: string, page = 1, pageSize = 25): Promise<PaginatedEnvelope<MissingGpcItem>> {
  const params = new URLSearchParams({ gln, page: String(page), pageSize: String(pageSize) });
  const response = await apiClient.get<PaginatedEnvelope<MissingGpcItem>>(`/data-quality/reports/missing-gpc?${params.toString()}`);
  return response.data;
}
